/**
 * RichTextEditor — editor TipTap reutilizable para artefactos oficiales.
 *
 * Diseño:
 *  - Modo `edit`: TipTap con BubbleMenu (toolbar contextual) + slash
 *    commands. Output JSON (`editor.getJSON()`) para diff estructural y
 *    chunking semántico futuro (Doc 08, Sprint C de Sources).
 *  - Modo `view`: TipTap read-only con la misma config. Garantiza que el
 *    render del viewer y del editor sean idénticos visualmente — single
 *    source of truth.
 *
 * Persistencia:
 *  - Acepta `value: RichTextValue` (TipTap JSON, string legacy, null,
 *    undefined). Normaliza a doc TipTap via `coerceToTipTapDoc`.
 *  - Emite `onChange(JSONContent)` con el doc actual. El parent
 *    decide cuándo persistir (típicamente debounced + en blur + al
 *    guardar el form).
 *  - Sync externo: cuando `value` cambia desde fuera (ej. carga
 *    inicial o rollback), llamamos `setContent(...)` con
 *    `emitUpdate: false` para NO disparar `onChange` en bucle.
 *
 * NO incluye en Sprint 1:
 *  - IA (autocomplete, suggest, rewrite). Llega en Sprint C de Sources.
 *  - Comments, suggestions, track-changes. Llegan con review flow
 *    (B.6.c.2).
 *  - Imágenes. Llegan con Azure Blob real (Fase F).
 *  - TOC sidebar. Llega cuando los artefactos largos lo pidan.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EditorContent, useEditor, type JSONContent } from '@tiptap/react'

import { BubbleToolbar } from './BubbleToolbar'
import { buildEditorExtensions } from './extensions'
import {
  filterSlashCommands,
  SLASH_COMMANDS,
  SlashMenu,
  type SlashCommand,
} from './SlashMenu'
import { coerceToTipTapDoc, type RichTextValue } from './value'

export interface RichTextEditorProps {
  value: RichTextValue
  onChange?: (value: JSONContent) => void
  /** `edit` (default) | `view` (read-only). El editor mismo es el que
   * renderiza ambos para garantizar visual parity con el viewer. */
  mode?: 'edit' | 'view'
  /** Texto en gris dentro del editor cuando está vacío. */
  placeholder?: string
  /** Class extra para el contenedor del prose (se concatena con
   * `prose-hiva`). Útil para limitar altura, agregar borde, etc. */
  className?: string
  /** Si true, el editor no se puede editar aunque mode='edit'. Útil
   * para deshabilitar mientras se persiste un save. */
  disabled?: boolean
  /** Callback opcional cuando el editor recibe foco (para mostrar
   * affordances en el contenedor padre). */
  onFocus?: () => void
}

interface SlashState {
  /** Texto entre el `/` y el caret (lo que sigue al slash). */
  query: string
  /** Posición caret donde inicia el `/` — para reemplazar al insertar. */
  fromPos: number
  position: { top: number; left: number }
  selectedIndex: number
}

export function RichTextEditor({
  value,
  onChange,
  mode = 'edit',
  placeholder,
  className,
  disabled = false,
  onFocus,
}: RichTextEditorProps) {
  const { t } = useTranslation('editor')
  const isEditable = mode === 'edit' && !disabled

  // Slash state
  const [slash, setSlash] = useState<SlashState | null>(null)
  const slashRef = useRef<SlashState | null>(null)
  useLayoutEffect(() => {
    slashRef.current = slash
  }, [slash])

  // Para evitar disparar onChange en el primer mount (TipTap dispara
  // onUpdate al setear contenido inicial).
  const isInitialMount = useRef(true)

  // Doc inicial — si el value es legacy string, lo convertimos.
  const initialDoc = useMemo(() => coerceToTipTapDoc(value), [])
   

  const extensions = useMemo(
    () => buildEditorExtensions({ placeholder: placeholder ?? '' }),
    [placeholder],
  )

  // ─── Slash detection ─────────────────────────────────────────────────
  const detectSlash = useCallback(
    (editor: import('@tiptap/react').Editor) => {
      const { from } = editor.state.selection
      // Texto desde inicio del bloque hasta el caret. Solo detectamos
      // slash si está al inicio de un párrafo o tras whitespace.
      const $from = editor.state.doc.resolve(from)
      const blockStart = $from.start($from.depth)
      const beforeCaret = editor.state.doc.textBetween(blockStart, from, '\n', '\n')
      const match = /(?:^|\s)\/([^\s]*)$/.exec(beforeCaret)
      if (!match) {
        if (slashRef.current) setSlash(null)
        return
      }
      const query = match[1] ?? ''
      const slashStartOffset = match.index + (match[0].startsWith('/') ? 0 : 1)
      const slashFromPos = blockStart + slashStartOffset
      // Posición visual del slash (top, left) para anclar el menu.
      const coords = editor.view.coordsAtPos(slashFromPos)
      const top = coords.bottom + 6
      const left = coords.left
      setSlash({
        query,
        fromPos: slashFromPos,
        position: { top, left },
        selectedIndex: 0,
      })
    },
    [],
  )

  const filteredCommands = useMemo<SlashCommand[]>(() => {
    if (!slash) return []
    return filterSlashCommands(SLASH_COMMANDS, slash.query, t)
  }, [slash, t])

  const closeSlash = useCallback(() => setSlash(null), [])

  // ─── Editor instance ─────────────────────────────────────────────────
  const editor = useEditor(
    {
      extensions,
      content: initialDoc,
      editable: isEditable,
      editorProps: {
        attributes: {
          class: [
            'prose-hiva max-w-none focus:outline-none',
            className ?? '',
          ]
            .filter(Boolean)
            .join(' '),
        },
        handleKeyDown(_view, event) {
          // Si el slash menu está abierto, capturamos navegación.
          const current = slashRef.current
          if (!current) return false
          const cmds = filterSlashCommands(SLASH_COMMANDS, current.query, t)
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setSlash({
              ...current,
              selectedIndex:
                cmds.length === 0
                  ? 0
                  : (current.selectedIndex + 1) % cmds.length,
            })
            return true
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault()
            setSlash({
              ...current,
              selectedIndex:
                cmds.length === 0
                  ? 0
                  : (current.selectedIndex - 1 + cmds.length) % cmds.length,
            })
            return true
          }
          if (event.key === 'Enter') {
            const cmd = cmds[current.selectedIndex]
            if (cmd) {
              event.preventDefault()
              applySlashCommand(cmd)
              return true
            }
          }
          if (event.key === 'Escape') {
            event.preventDefault()
            closeSlash()
            return true
          }
          return false
        },
      },
      onUpdate({ editor: ed }) {
        if (isInitialMount.current) {
          isInitialMount.current = false
          return
        }
        if (onChange) onChange(ed.getJSON())
        detectSlash(ed)
      },
      onSelectionUpdate({ editor: ed }) {
        detectSlash(ed)
      },
      onFocus() {
        if (onFocus) onFocus()
      },
    },
    // Recreamos el editor si cambian las extensions (placeholder).
    [extensions],
  )

  // ─── Apply slash command ─────────────────────────────────────────────
  const applySlashCommand = useCallback(
    (cmd: SlashCommand) => {
      if (!editor) return
      const current = slashRef.current
      if (!current) {
        cmd.apply(editor)
        return
      }
      // Borrar el `/query` antes de aplicar el comando.
      const { from } = editor.state.selection
      editor
        .chain()
        .focus()
        .deleteRange({ from: current.fromPos, to: from })
        .run()
      cmd.apply(editor)
      closeSlash()
    },
    [editor, closeSlash],
  )

  // ─── Sync externo: editable cambia ───────────────────────────────────
  useEffect(() => {
    if (!editor) return
    editor.setEditable(isEditable)
  }, [editor, isEditable])

  // ─── Sync externo: value cambia desde fuera ──────────────────────────
  // Esto pasa cuando el parent reemplaza el value por una versión nueva
  // (ej. al cargar otro artifact, hacer rollback, recibir update IA).
  // NO disparamos onChange en este path — usamos `emitUpdate: false`.
  useEffect(() => {
    if (!editor) return
    const current = editor.getJSON()
    const incoming = coerceToTipTapDoc(value)
    if (areDocsShallowEqual(current, incoming)) return
    isInitialMount.current = true
    editor.commands.setContent(incoming, { emitUpdate: false })
  }, [editor, value])

  // Click en el padding del wrapper (área "muerta" entre el borde y el
  // texto) debería enfocar el editor — UX de textarea. Solo aplica si
  // el click es directamente en el wrapper (no en un descendiente como
  // un botón del bubble menu o un link dentro de la prosa).
  const handleWrapperMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (mode !== 'edit') return
      if (e.target !== e.currentTarget) return
      e.preventDefault()
      editor?.commands.focus('end')
    },
    [editor, mode],
  )

  // ─── Render ──────────────────────────────────────────────────────────
  if (!editor) {
    // Mientras useEditor se inicializa (un tick).
    return (
      <div className="rounded-md border border-line bg-canvas px-3 py-3 font-mono text-xs text-ink-subtle">
        {t('loading')}
      </div>
    )
  }

  return (
    <div
      onMouseDown={handleWrapperMouseDown}
      className={
        mode === 'edit'
          ? 'rounded-md border border-line bg-canvas px-3 py-2 focus-within:border-brand transition-colors cursor-text'
          : 'rounded-md bg-canvas px-3 py-2'
      }
    >
      <EditorContent editor={editor} />
      {mode === 'edit' && editor ? <BubbleToolbar editor={editor} /> : null}
      {slash && filteredCommands && mode === 'edit' ? (
        <SlashMenu
          commands={filteredCommands}
          selectedIndex={slash.selectedIndex}
          onSelect={applySlashCommand}
          position={slash.position}
        />
      ) : null}
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────
/** Compare shallow para evitar re-aplicar el mismo doc (que sí tiene
 * referencias distintas pero contenido idéntico). Comparamos
 * stringified — más barato que recursión profunda y suficiente para
 * detectar el caso "el parent re-renderizó pero el doc no cambió". */
function areDocsShallowEqual(a: JSONContent, b: JSONContent): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}
